// Accepting a knowledge-hub offer, React-free (like the run lifecycle) so the
// retry policy is unit-testable. Accepting races the just-finished run's
// teardown/persistence: the offer may not be persisted yet, or the thread's
// prior run hasn't released. Both are transient — retry once after a short delay.

export const OFFER_RETRY_DELAY_MS = 1000;
export const RETRYABLE_OFFER_ERROR_CODES: ReadonlySet<string> = new Set([
  'unknown_offer',
  'run_already_active',
]);

export interface AcceptOfferDeps {
  /** One accept attempt; its outcome is read back through `lastErrorCode`. */
  run: () => Promise<void>;
  /** The server error code the latest attempt ended with, if any. */
  lastErrorCode: () => string | undefined;
  /** True once the conversation moved on — a stale retry must not run against it. */
  isCancelled?: () => boolean;
  /** Held true across both attempts so the composer can't send into the retry gap. */
  setInFlight: (inFlight: boolean) => void;
  sleep: (ms: number) => Promise<void>;
}

/** Run the accept, retrying once on a transient code. Resolves true when rejected outright. */
export async function acceptOffer({
  run,
  lastErrorCode,
  isCancelled,
  setInFlight,
  sleep,
}: AcceptOfferDeps): Promise<boolean> {
  setInFlight(true);
  try {
    await run();
    if (RETRYABLE_OFFER_ERROR_CODES.has(lastErrorCode() ?? '')) {
      await sleep(OFFER_RETRY_DELAY_MS);
      if (isCancelled?.()) return false;
      await run();
    }
    return isCancelled?.() ? false : Boolean(lastErrorCode());
  } finally {
    setInFlight(false);
  }
}
